import { Button, TextInput } from "@mantine/core";
import {
  IconLayoutGridFilled,
  IconList,
  IconPlus,
  IconSearch,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";

import WarehouseCard from "@/components/WarehouseCard";
import { Table } from "antd";
import type { ColumnsType } from "antd/es/table/interface";
import { orpcClient } from "@/lib/orpc-client";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

function WarehouseManagement() {
  const { t } = useTranslation();
  // list or grid
  const [layout, setLayout] = useState("list");
  const [warehouseData, setWarehouseData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const navigate = useNavigate();

  const handleAddWarehouse = () => {
    navigate("/hrMainPage/addWarehouse");
  };

  const handleLayoutChange = (layout) => {
    setLayout(layout);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;

    const filtered = warehouseData.filter((item) =>
      item?.name?.toLowerCase().startsWith(value.trim().toLowerCase())
    );

    if (value === "") {
      setFilteredData(warehouseData);
    } else {
      setFilteredData(filtered);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await orpcClient.warehouse.list({});
        setWarehouseData(result as any);
        setFilteredData(result as any);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const columns: ColumnsType<any> = [
    {
      title: t("warehouse.warehouseName") || "倉庫名稱",
      dataIndex: "name",
      key: "name",
      fixed: "left" as const,
      sorter: (a: any, b: any) =>
        (a?.name || "").localeCompare(b?.name || ""),
      render: (text: string) => text || "",
    },
    {
      title: t("warehouse.description"),
      dataIndex: "description",
      key: "description",
      render: (text: string) => text || "",
    },
    {
      title: t("warehouse.warehouseAddress") || "倉庫地址",
      dataIndex: "address",
      key: "address",
      render: (address: any) =>
        address
          ? `${address.city || ""}${address.district || ""}${address.address || ""}`
          : "",
    },
    {
      title: t("warehouse.enable"),
      dataIndex: "enable",
      key: "enable",
      render: (enable: any) =>
        enable === true
          ? t("warehouse.enabledValue")
          : enable === false
            ? t("warehouse.disabledValue")
            : enable,
    },
  ];

  const toggleButton = (
    value: string,
    Icon: React.ComponentType<{ size?: number | string }>
  ) => (
    <button
      type="button"
      onClick={() => handleLayoutChange(value)}
      className={`flex h-9 w-9 items-center justify-center rounded-lg border ${
        layout === value
          ? "border-blue-200 bg-blue-50 text-blue-600"
          : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
      }`}
    >
      <Icon size={18} />
    </button>
  );

  return !warehouseData || warehouseData?.length === 0 ? (
    <div className="flex min-h-screen items-center justify-center p-8">
      <div className="w-[487px] rounded-xl bg-white px-16 py-20 shadow-sm">
        <div className="text-center" style={{ color: "#6F6F6F" }}>
          {t("hrMain.noWarehouseDataYet") || "您尚未新增倉儲資料！你可以試試："}
        </div>
        <div className="flex text-center">
          <Button
            variant="outline"
            color="gray"
            style={{ width: "100%", margin: "1rem" }}
            radius="md"
          >
            {t("departmentManagement.quickCreate") || "快速新增"}
          </Button>
          <Button
            variant="outline"
            color="gray"
            style={{ width: "100%", margin: "1rem" }}
            radius="md"
            onClick={handleAddWarehouse}
          >
            {t("hrMain.manualAdd") || "手動新增"}
          </Button>
        </div>
      </div>
    </div>
  ) : (
    <div className="p-8">
      <div className="mx-auto max-w-6xl space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-4xl font-bold" style={{ color: "#6F6F6F" }}>
              {t("warehouse.management") || "倉庫管理"}
            </div>
            <div className="mt-2 text-sm text-gray-500">
              {t("warehouse.list") || "倉庫列表"}
            </div>
          </div>
          <Button
            variant="filled"
            color="gray"
            radius="md"
            leftSection={<IconPlus size={16} />}
            onClick={handleAddWarehouse}
          >
            {t("warehouse.add")}
          </Button>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-between gap-4 border-b border-gray-200 px-6 py-4">
            <TextInput
              className="w-full max-w-md"
              leftSection={<IconSearch size={16} />}
              placeholder={t("warehouse.searchPlaceholder")}
              onChange={handleInputChange}
            />
            <div className="flex gap-2">
              {toggleButton("list", IconList)}
              {toggleButton("grid", IconLayoutGridFilled)}
            </div>
          </div>

          {layout === "list" ? (
            <div className="p-4">
              <Table
                columns={columns}
                dataSource={filteredData}
                rowKey="id"
                onRow={(record: any) => ({
                  onClick: () =>
                    navigate(`/hrMainPage/warehouse/${record.id}`),
                  style: { cursor: "pointer" },
                })}
                scroll={{ x: "max-content" }}
                pagination={{
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "25", "50", "100"],
                  showQuickJumper: false,
                }}
                bordered
                sticky
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredData?.map((data) => {
                return (
                  <WarehouseCard
                    key={data.id}
                    props={data}
                    onClick={() => navigate(`/hrMainPage/warehouse/${data.id}`)}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default WarehouseManagement;
